'use client'

import { getCookie, setCookie } from 'cookies-next'
import { atom, useAtom } from 'jotai'
import { X } from 'lucide-react'
import { useEffect } from 'react'
import { usePortfolioView } from '../stores/portfolio-drawer/actions'
import { cn } from '../utils/styles'
import { Button } from './ui/button'

interface AffiliateCardProps {
  className?: string
}

const cookieKey = 'affiliate-card-dismissed'

const loadDismissedState = (): boolean => {
  const cookie = getCookie(cookieKey)
  return cookie === 'true'
}

const saveDismissedState = (dismissed: boolean): void => {
  setCookie(cookieKey, dismissed.toString(), {
    maxAge: 31536000, // 1 year
  })
}

const affiliateCardDismissedAtom = atom<boolean>(false)

export const AffiliateCard = ({ className }: AffiliateCardProps) => {
  const [dismissed, setDismissed] = useAtom(affiliateCardDismissedAtom)
  const { updateView } = usePortfolioView()

  // Load dismissed state from cookie on mount
  useEffect(() => {
    setDismissed(loadDismissedState())
  }, [setDismissed])

  const handleDismiss = () => {
    setDismissed(true)
    saveDismissedState(true)
  }

  if (dismissed) {
    return null
  }

  return (
    <div
      className={cn(
        'w-full rounded-3xl shadow-xl overflow-hidden light bg-orange-300 bg-[url(https://s3.polaris.app/artworks/affiliate-card.png)] bg-[length:auto_100%] bg-no-repeat bg-[right_-30%_center] sm:bg-[right_-130%_center] md:bg-[right_-140%_center] xl:bg-[right_-180%_center] 2xl:bg-[right_-90%_center] transition-[background-position] duration-300',
        className,
      )}
    >
      <div className='relative flex items-center w-full p-4 gap-6 bg-gradient-to-r from-orange-300 from-60% to-orange-300/0 md:from-50%'>
        <Button
          className='absolute top-4 right-4 z-20 opacity-40 hover:opacity-100'
          variant='ghost'
          size='sm'
          onClick={handleDismiss}
          type='button'
        >
          <X className='text-link' />
        </Button>

        {/* Left content */}
        <div className='flex flex-col flex-1 z-10 gap-1'>
          <h5 className='font-serif font-extralight text-primary text-3xl'>
            When we win, you win.
          </h5>

          <p className='mb-4 text-sm text-secondary max-w-64'>
            Share with friends and future perks may be tied to early activity.
          </p>

          <Button
            variant='primary'
            size='sm'
            onClick={() => updateView({ view: 'referrals' })}
            className='self-start'
          >
            Create your link
          </Button>
        </div>
      </div>
    </div>
  )
}
